import { Box, Flex, Icon, Stack, Text, type StackProps } from '@chakra-ui/react';

import { Panel } from '../Panel';

/**
 * Карточка настроек: шапка с иконкой и строки под ней.
 *
 * Заводится общим компонентом, потому что этот узор в настройках уже был написан трижды и трижды
 * по-разному: канал уведомлений на одной поверхности с отступом 20, раздел «О Teamplay» на другой
 * с отступом 16, карточка аккаунта на третьей вообще без разделителей. Три соседних экрана
 * настроек выглядели как из разных продуктов, хотя показывают одно и то же - предмет, у которого
 * есть заголовок и несколько переключаемых строк.
 *
 * За образец взят экран уведомлений: там узор сложился первым и читается лучше всех.
 */
export const SettingsCard = ({ children, ...rest }: React.ComponentProps<typeof Panel>) => (
  <Panel variant="object" w="100%" overflow="hidden" {...rest}>
    {children}
  </Panel>
);

/** Плитка с иконкой слева: шапка карточки и строка настройки рисуют её одинаково. */
const IconTile = ({ icon }: { icon: React.ElementType }) => (
  <Flex
    w={8}
    h={8}
    borderRadius="l2"
    bg="bg.emphasized"
    align="center"
    justify="center"
    color="fg.emphasized"
    flexShrink={0}
  >
    <Icon as={icon} boxSize={4.5} />
  </Flex>
);

type HeaderProps = {
  icon?: React.ElementType;
  title: string;
  /** Вторая строка под названием: ник аккаунта, адрес, что угодно уточняющее. */
  subtitle?: string;
  /** Правый край: статус, кнопка, что угодно одно. */
  action?: React.ReactNode;
  /** Preserve the title on narrow cards by moving the entire action below it. */
  wrapActionOnCompact?: boolean;
  /** Keep a divider only when the header has a body below it. */
  divided?: boolean;
};

const SettingsCardHeader = ({
  icon,
  title,
  subtitle,
  action,
  wrapActionOnCompact,
  divided = true,
}: HeaderProps) => (
  <Box css={wrapActionOnCompact ? { containerType: 'inline-size' } : undefined}>
    <Flex
      css={
        wrapActionOnCompact
          ? {
              '@container (max-width: 20rem)': {
                flexWrap: 'wrap',
                '& > [data-settings-header-action]': {
                  width: '100%',
                  '& > button': { width: '100%' },
                },
              },
            }
          : undefined
      }
      align="center"
      gap={3}
      px={5}
      py={3.5}
      // Линия границей, а не внутренней тенью: разделитель в продукте рисуется одним способом.
      borderBottomWidth={divided ? '1px' : undefined}
      borderBottomColor="border"
    >
      {icon ? <IconTile icon={icon} /> : null}
      <Stack gap={0} flex={1} minW={0}>
        <Text textStyle="title" truncate>
          {title}
        </Text>
        {subtitle ? (
          <Text textStyle="caption" color="fg.muted" truncate>
            {subtitle}
          </Text>
        ) : null}
      </Stack>
      {action ? (
        <Box data-settings-header-action="" flexShrink={0}>
          {action}
        </Box>
      ) : null}
    </Flex>
  </Box>
);

type SettingRowProps = Omit<StackProps, 'title'> & {
  /** Плитка с иконкой слева, как в шапке карточки: платформа устройства, тип данных. */
  icon?: React.ElementType;
  /** Необязателен: строка бывает и просто пояснением без своего управления. */
  title?: string;
  description?: React.ReactNode;
  /** Правый край строки: переключатель, кнопка, стрелка. */
  action?: React.ReactNode;
  dimmed?: boolean;
  /** Под строкой во всю ширину: кнопка, которая чинит то, о чём говорит пояснение. */
  children?: React.ReactNode;
};

/**
 * Строка настройки: название, пояснение и управление справа.
 *
 * Отдельно от карточки, потому что настройки живут не только на экранах настроек. Окно настроек
 * заявки и окно «заявка опубликована» собирали ту же строку руками, четыре раза и в трёх
 * вариантах: название кеглем подписи раздела, пояснение то `body`, то `caption`, в одном окне
 * два размера пояснений подряд. Та же настройка на экране уведомлений и в окне заявки
 * выглядела как две разные.
 */
export const SettingRow = ({
  icon,
  title,
  description,
  action,
  dimmed,
  children,
  ...rest
}: SettingRowProps) => {
  const text = (
    <Stack gap={0.5} flex={1} minW={0}>
      {title ? <Text textStyle="title">{title}</Text> : null}
      {description ? (
        <Text
          // Пояснение со вложенными строками (место, время) не может быть абзацем.
          as={typeof description === 'string' ? undefined : 'div'}
          textStyle="caption"
          lineHeight="moderate"
          color="fg.secondary"
        >
          {description}
        </Text>
      ) : null}
    </Stack>
  );

  return (
    <Stack gap={2} w="100%" opacity={dimmed ? 0.55 : 1} {...rest}>
      <Flex align="center" gap={5}>
        {icon ? (
          <Flex align="center" gap={3} flex={1} minW={0}>
            <IconTile icon={icon} />
            {text}
          </Flex>
        ) : (
          text
        )}
        {action ? <Box flexShrink={0}>{action}</Box> : null}
      </Flex>
      {children}
    </Stack>
  );
};

type RowProps = SettingRowProps & {
  /** Разделитель сверху. У первой строки его нет: под ней уже линия шапки. */
  divided?: boolean;
};

const SettingsCardRow = ({ divided = true, ...row }: RowProps) => (
  <SettingRow
    px={5}
    py={4}
    borderTopWidth={divided ? '1px' : undefined}
    borderTopColor="border.subtle"
    {...row}
  />
);

/** Пояснение под строками: живёт в той же карточке, но переключать в нём нечего. */
const SettingsCardNote = ({ children }: { children: React.ReactNode }) => (
  <Box px={5} pb={5}>
    {children}
  </Box>
);

SettingsCard.Header = SettingsCardHeader;
SettingsCard.Row = SettingsCardRow;
SettingsCard.Note = SettingsCardNote;
